import { PropsWithChildren, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactModal from 'react-modal';
import { useRootContext } from '../../../UnicronApp/RootContext';
import { useId } from '../../hooks/useId';
import { TRANSITION_LENGTH } from '../../styles/constants';
import CloseIcon from './CloseIcon.svg';
import {
  GlobalClassNames,
  GlobalStyleSheet,
  ModalContent,
  StyledCloseIcon,
  STANDARD_WIDTH,
} from './StandardModal.styles';

export type Props = PropsWithChildren<ReactModal.Props> & {
  className?: string;
  width?: string;
  height?: string;
  padContainer?: boolean;
  showCloseButton?: boolean;
  closeOnOuterClick?: boolean;
};

export const StandardModal = ({
  children,
  width = STANDARD_WIDTH,
  height = 'unset',
  padContainer = true,
  showCloseButton = true,
  onRequestClose,
  closeOnOuterClick = true,
  isOpen,
  ...props
}: Props) => {
  const { i18n } = useTranslation();
  const rootRef = useRootContext()?.current;
  const id = useId();
  const contentClassName = `${GlobalClassNames.CONTENT}_${id}`;
  const [opened, setOpened] = useState<boolean>(isOpen);

  // Prevent the component rendering till it's shown
  // Keep it rendered so that we can show the closed transition
  useEffect(() => {
    if (!opened && isOpen) {
      setOpened(true);
    }
  }, [isOpen, opened]);

  if (!opened) {
    return null;
  }

  return (
    <>
      <GlobalStyleSheet
        width={width}
        height={height}
        padContainer={padContainer}
        contentClassName={contentClassName}
      />
      <ReactModal
        shouldCloseOnEsc={false}
        ariaHideApp={!!rootRef}
        appElement={rootRef || undefined}
        className={{
          afterOpen: GlobalClassNames.AFTER_OPEN,
          beforeClose: GlobalClassNames.BEFORE_CLOSE,
          base: contentClassName,
        }}
        closeTimeoutMS={TRANSITION_LENGTH}
        overlayClassName={{
          afterOpen: GlobalClassNames.AFTER_OPEN,
          beforeClose: GlobalClassNames.BEFORE_CLOSE,
          base: GlobalClassNames.OVERLAY,
        }}
        bodyOpenClassName={GlobalClassNames.BODY}
        onRequestClose={closeOnOuterClick ? onRequestClose : undefined}
        isOpen={isOpen}
        {...props}
        aria={{ labelledby: 'modal_label_aria' }}
      >
        <ModalContent dir={i18n.dir()}>
          <span style={{ display: 'none' }} id="modal_label_aria">
            Popup modal
          </span>
          {showCloseButton && <StyledCloseIcon as={CloseIcon} onClick={onRequestClose} />}
          {children}
        </ModalContent>
      </ReactModal>
    </>
  );
};

/** A selector which applies to the content element after it is open */
export const TRANSITION_AFTER_OPEN = "&[class*='--after-open']";

/** A selector which applies to the content element before it finishes closing */
export const TRANSITION_BEFORE_CLOSE = "&[class*='--before-close']";
